/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { PageResource } from '../models/page-resource';
import { ProductImportViolation } from '../models/product-import-violation';
import { Resource } from '../models/resource';

/**
 * Endpoints for Import related resources.
 */
@Injectable({
  providedIn: 'root',
})
export class ImportService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Path part for operation getImage
   */
  static readonly GetImagePath = '/import/images/{key}';

  /**
   * Get image.
   *
   * Returns the specified image reference.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getImage()` instead.
   *
   * This method doesn't expect any request body.
   */
  getImage$Response(
    params: {
      /**
       * The id (i.e path/name) of imported image to get the reference
       */
      key: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Resource>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ImportService.GetImagePath,
      'get'
    );
    if (params) {
      rb.path('key', params.key, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Resource>;
        })
      );
  }

  /**
   * Get image.
   *
   * Returns the specified image reference.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getImage$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getImage(
    params: {
      /**
       * The id (i.e path/name) of imported image to get the reference
       */
      key: string;
    },
    context?: HttpContext
  ): Observable<Resource> {
    return this.getImage$Response(params, context).pipe(
      map((r: StrictHttpResponse<Resource>) => r.body as Resource)
    );
  }

  /**
   * Path part for operation uploadImage
   */
  static readonly UploadImagePath = '/import/images/{key}';

  /**
   * Upload image.
   *
   * Uploads a new content for the given id or if there isn't any images for the given ID then a new one is imported.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `uploadImage()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  uploadImage$Response(
    params: {
      /**
       * The id (i.e path/name) of imported image to upload
       */
      key: string;
      body?: {
        /**
         * The image file resource
         */
        file: Blob;
      };
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ImportService.UploadImagePath,
      'put'
    );
    if (params) {
      rb.path('key', params.key, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Upload image.
   *
   * Uploads a new content for the given id or if there isn't any images for the given ID then a new one is imported.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `uploadImage$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  uploadImage(
    params: {
      /**
       * The id (i.e path/name) of imported image to upload
       */
      key: string;
      body?: {
        /**
         * The image file resource
         */
        file: Blob;
      };
    },
    context?: HttpContext
  ): Observable<void> {
    return this.uploadImage$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation deleteImage
   */
  static readonly DeleteImagePath = '/import/images/{key}';

  /**
   * Delete image.
   *
   * Deletes the images for the given ID.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteImage()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteImage$Response(
    params: {
      /**
       * The id (i.e path/name) of imported image to delete
       */
      key: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ImportService.DeleteImagePath,
      'delete'
    );
    if (params) {
      rb.path('key', params.key, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Delete image.
   *
   * Deletes the images for the given ID.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteImage$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteImage(
    params: {
      /**
       * The id (i.e path/name) of imported image to delete
       */
      key: string;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.deleteImage$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getExcel
   */
  static readonly GetExcelPath = '/import/excels/{key}';

  /**
   * Get excel file.
   *
   * Returns the specified excel file reference.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getExcel()` instead.
   *
   * This method doesn't expect any request body.
   */
  getExcel$Response(
    params: {
      /**
       * The id (i.e path/name) of imported excel file to get the reference
       */
      key: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Resource>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ImportService.GetExcelPath,
      'get'
    );
    if (params) {
      rb.path('key', params.key, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Resource>;
        })
      );
  }

  /**
   * Get excel file.
   *
   * Returns the specified excel file reference.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getExcel$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getExcel(
    params: {
      /**
       * The id (i.e path/name) of imported excel file to get the reference
       */
      key: string;
    },
    context?: HttpContext
  ): Observable<Resource> {
    return this.getExcel$Response(params, context).pipe(
      map((r: StrictHttpResponse<Resource>) => r.body as Resource)
    );
  }

  /**
   * Path part for operation uploadExcel
   */
  static readonly UploadExcelPath = '/import/excels/{key}';

  /**
   * Upload excel file.
   *
   * Uploads a new content for the given id or if there isn't any excel files for the given ID then a new one is imported.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `uploadExcel()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  uploadExcel$Response(
    params: {
      /**
       * The id (i.e path/name) of imported excel file to upload
       */
      key: string;
      body?: {
        /**
         * The image file resource
         */
        file: Blob;
      };
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ImportService.UploadExcelPath,
      'put'
    );
    if (params) {
      rb.path('key', params.key, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Upload excel file.
   *
   * Uploads a new content for the given id or if there isn't any excel files for the given ID then a new one is imported.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `uploadExcel$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  uploadExcel(
    params: {
      /**
       * The id (i.e path/name) of imported excel file to upload
       */
      key: string;
      body?: {
        /**
         * The image file resource
         */
        file: Blob;
      };
    },
    context?: HttpContext
  ): Observable<void> {
    return this.uploadExcel$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation deleteExcel
   */
  static readonly DeleteExcelPath = '/import/excels/{key}';

  /**
   * Delete excel file.
   *
   * Deletes the excel file for the given ID.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteExcel()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteExcel$Response(
    params: {
      /**
       * The id (i.e path/name) of imported excel file to delete
       */
      key: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ImportService.DeleteExcelPath,
      'delete'
    );
    if (params) {
      rb.path('key', params.key, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Delete excel file.
   *
   * Deletes the excel file for the given ID.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteExcel$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteExcel(
    params: {
      /**
       * The id (i.e path/name) of imported excel file to delete
       */
      key: string;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.deleteExcel$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation listImageReferences
   */
  static readonly ListImageReferencesPath = '/import/images';

  /**
   * List imported images.
   *
   * Gets the list of imported image names of merchant.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listImageReferences()` instead.
   *
   * This method doesn't expect any request body.
   */
  listImageReferences$Response(
    params?: {},
    context?: HttpContext
  ): Observable<StrictHttpResponse<PageResource>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ImportService.ListImageReferencesPath,
      'get'
    );
    if (params) {
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PageResource>;
        })
      );
  }

  /**
   * List imported images.
   *
   * Gets the list of imported image names of merchant.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listImageReferences$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listImageReferences(
    params?: {},
    context?: HttpContext
  ): Observable<PageResource> {
    return this.listImageReferences$Response(params, context).pipe(
      map((r: StrictHttpResponse<PageResource>) => r.body as PageResource)
    );
  }

  /**
   * Path part for operation uploadImages
   */
  static readonly UploadImagesPath = '/import/images';

  /**
   * Upload images.
   *
   * Uploads multiple images if any images already exist with an identifier, then those ones will be overwritten.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `uploadImages()` instead.
   *
   * This method doesn't expect any request body.
   */
  uploadImages$Response(
    params: {
      /**
       * The image file resources
       */
      file: Array<Blob>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ImportService.UploadImagesPath,
      'post'
    );
    if (params) {
      rb.query('file', params.file, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Upload images.
   *
   * Uploads multiple images if any images already exist with an identifier, then those ones will be overwritten.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `uploadImages$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  uploadImages(
    params: {
      /**
       * The image file resources
       */
      file: Array<Blob>;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.uploadImages$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation listExcels
   */
  static readonly ListExcelsPath = '/import/excels';

  /**
   * List imported excel files.
   *
   * Gets the list of imported excel file names of merchant.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listExcels()` instead.
   *
   * This method doesn't expect any request body.
   */
  listExcels$Response(
    params?: {},
    context?: HttpContext
  ): Observable<StrictHttpResponse<PageResource>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ImportService.ListExcelsPath,
      'get'
    );
    if (params) {
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PageResource>;
        })
      );
  }

  /**
   * List imported excel files.
   *
   * Gets the list of imported excel file names of merchant.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listExcels$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listExcels(params?: {}, context?: HttpContext): Observable<PageResource> {
    return this.listExcels$Response(params, context).pipe(
      map((r: StrictHttpResponse<PageResource>) => r.body as PageResource)
    );
  }

  /**
   * Path part for operation uploadExcels
   */
  static readonly UploadExcelsPath = '/import/excels';

  /**
   * Upload excel files.
   *
   * Uploads multiple images if any excel files already exist with an identifier, then those ones will be overwritten.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `uploadExcels()` instead.
   *
   * This method doesn't expect any request body.
   */
  uploadExcels$Response(
    params: {
      /**
       * The excel file resources
       */
      file: Array<Blob>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ImportService.UploadExcelsPath,
      'post'
    );
    if (params) {
      rb.query('file', params.file, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Upload excel files.
   *
   * Uploads multiple images if any excel files already exist with an identifier, then those ones will be overwritten.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `uploadExcels$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  uploadExcels(
    params: {
      /**
       * The excel file resources
       */
      file: Array<Blob>;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.uploadExcels$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation validateExcel
   */
  static readonly ValidateExcelPath = '/import/excels/{key}/validate';

  /**
   * Validate excel file.
   *
   * Validates the specified excel file and returns a JSON array with import violations.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `validateExcel()` instead.
   *
   * This method doesn't expect any request body.
   */
  validateExcel$Response(
    params: {
      /**
       * The id (i.e path/name) of imported excel file to validate
       */
      key: string;

      /**
       * The merchantId in case of EASYLOT_ADMIN
       */
      merchantIdParam?: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<ProductImportViolation>>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ImportService.ValidateExcelPath,
      'get'
    );
    if (params) {
      rb.path('key', params.key, {});
      rb.query('merchantIdParam', params.merchantIdParam, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Array<ProductImportViolation>>;
        })
      );
  }

  /**
   * Validate excel file.
   *
   * Validates the specified excel file and returns a JSON array with import violations.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `validateExcel$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  validateExcel(
    params: {
      /**
       * The id (i.e path/name) of imported excel file to validate
       */
      key: string;

      /**
       * The merchantId in case of EASYLOT_ADMIN
       */
      merchantIdParam?: string;
    },
    context?: HttpContext
  ): Observable<Array<ProductImportViolation>> {
    return this.validateExcel$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<Array<ProductImportViolation>>) =>
          r.body as Array<ProductImportViolation>
      )
    );
  }

  /**
   * Path part for operation importExcel
   */
  static readonly ImportExcelPath = '/import/excels/{key}/import';

  /**
   * Apply import product on excel file.
   *
   * Returns the specified image reference.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `importExcel()` instead.
   *
   * This method doesn't expect any request body.
   */
  importExcel$Response(
    params: {
      /**
       * The id (i.e path/name) of imported image to aplye product import
       */
      key: string;

      /**
       * The merchantId in case of EASYLOT_ADMIN
       */
      merchantIdParam?: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<ProductImportViolation>>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ImportService.ImportExcelPath,
      'get'
    );
    if (params) {
      rb.path('key', params.key, {});
      rb.query('merchantIdParam', params.merchantIdParam, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Array<ProductImportViolation>>;
        })
      );
  }

  /**
   * Apply import product on excel file.
   *
   * Returns the specified image reference.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `importExcel$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  importExcel(
    params: {
      /**
       * The id (i.e path/name) of imported image to aplye product import
       */
      key: string;

      /**
       * The merchantId in case of EASYLOT_ADMIN
       */
      merchantIdParam?: string;
    },
    context?: HttpContext
  ): Observable<Array<ProductImportViolation>> {
    return this.importExcel$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<Array<ProductImportViolation>>) =>
          r.body as Array<ProductImportViolation>
      )
    );
  }
}
